import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from "firebase/auth";
import { doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { DEFAULT_ROLES } from '../constants/permissions';
import { profilePermissions } from '../utils/permissionOverrides';
import { useModal } from './ModalContext';

import logger from '../utils/logger';
import { adminService } from '../services/adminService';
import { USE_CONTRACTS_V2 } from '../services/contractsV2';
import { sessionUtils } from '../utils/sessionSecurity';
import { viewAsRole, rolesDraft } from '../utils/viewAsRole';

const AuthContext = React.createContext();

// Signed out after this long with no activity, every role (owner, 17 Oct 2026: was 1 h, admin 2 h)
export const IDLE_LOGOUT_MS = 2 * 60 * 60 * 1000;
// The last activity in ANY Yelo tab: the sign-out is shared by every tab (Firebase), so an idle second tab used to
// sign out the one in use
const ACTIVITY_KEY = 'yelo_last_activity';
const sharedActivity = () => {
    try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
};
const shareActivity = (at) => {
    try { localStorage.setItem(ACTIVITY_KEY, String(at)); } catch { /* private window: this tab alone */ }
};

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failedAttempts, setFailedAttempts] = useState([]);
    const { showAlert } = useModal();
    
    const timeoutIdRef = React.useRef(null);
    const roleRef = React.useRef('viewer');
    const lastActivityRef = React.useRef(Date.now());

    async function login(email, password) {
        try {
            // Check rate limiting (time-based)
            const now = Date.now();
            const recentAttempts = failedAttempts.filter(time => now - time < 15 * 60 * 1000);
            if (recentAttempts.length >= 5) {
                throw new Error('Too many login attempts. Please try again later.');
            }

            const result = await signInWithEmailAndPassword(auth, email, password);
            
            // Clear failed attempts on successful login
            setFailedAttempts([]);
            
            // Create secure session
            const sessionToken = sessionUtils.generateSessionToken();
            sessionUtils.setSecureSession(sessionToken, result.user.uid);
            
            // Log successful login (without PII)
            adminService.logUserAction('LOGIN', 'User logged in successfully', {
                uid: result.user.uid,
                timestamp: Date.now()
            });
            
            return result;
        } catch (error) {
            // Track failed attempts
            const newAttempts = [...failedAttempts, Date.now()];
            setFailedAttempts(newAttempts);
            
            logger.warn(`Login failed: ${error.code}`);
            throw error;
        }
    }

    // logout is STABLE (the session timer and its 5 window listeners depend on it): it reads the user
    // from a ref instead of the render it was made in, so the listeners are attached once, not every render
    const currentUserRef = useRef(currentUser);
    currentUserRef.current = currentUser;
    const logout = useCallback(() => {
        const user = currentUserRef.current;
        let logged = null;
        if (user) {
            logged = adminService.logUserAction('LOGOUT', 'User logged out', {
                uid: user.uid
            });
        }
        
        // Clear secure session
        sessionUtils.clearSession();
        // an admin's "Show the app as this role" ends with the sign-out (the next sign-in in this tab starts as itself)
        viewAsRole.clear();
        rolesDraft.clear();                 // and the roles page's unsaved edits kept for it
        
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
        
        // The store's activity log (USE_CONTRACTS_V2) is stamped by the engine from the signed-in user's token, so
        // the LOGOUT line is sent BEFORE signOut (after it the request carries no token and is refused). The log
        // never throws and gives up after a few seconds, so signing out never waits long.
        if (USE_CONTRACTS_V2 && logged) return logged.then(() => signOut(auth));
        return signOut(auth);
    }, []);

    function resetPassword(email) {
        // Enhanced email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            throw new Error('Invalid email format');
        }
        
        return sendPasswordResetEmail(auth, email);
    }

    // Enhanced session timer with security checks and inactivity reset
    const resetTimer = React.useCallback(() => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }
        
        const sessionTimeout = IDLE_LOGOUT_MS;

        timeoutIdRef.current = setTimeout(() => {
            const now = Date.now();
            // Verify if there was recent activity (in this tab or another Yelo tab) before forcing logout
            const last = Math.max(lastActivityRef.current, sharedActivity());
            if (now - last < sessionTimeout - 60000) {
                 resetTimer(); // Wait more if active
                 return;
            }

            logout();
            showAlert({
                title: 'Session Expired',
                message: 'Your session has expired due to inactivity. Please login again.',
                buttonLabel: 'OK',
                isError: true
            });
        }, sessionTimeout);
    }, [logout, showAlert]);

    useEffect(() => {
        let throttleTimer = null;
        let sharedAt = 0;
        const handleActivity = () => {
            const now = Date.now();
            if (now - sharedAt > 15000) { sharedAt = now; shareActivity(now); }
            lastActivityRef.current = now;
            if (throttleTimer) return;
            throttleTimer = setTimeout(() => {
                resetTimer();
                throttleTimer = null;
            }, 30000); // Throttle timer reset to at most once per 30 seconds
        };

        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(event => window.addEventListener(event, handleActivity));

        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
            if (throttleTimer) clearTimeout(throttleTimer);
        };
    }, [resetTimer]);

    const startSessionTimer = (role) => {
        roleRef.current = role;
        lastActivityRef.current = Date.now();
        shareActivity(lastActivityRef.current);
        resetTimer();
    };

    useEffect(() => {
        let unsubscribeSnapshot = null;
        let unsubscribeMaintenance = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            // Clean up previous snapshot listener
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
                unsubscribeSnapshot = null;
            }
            
            // Clean up previous maintenance listener
            if (unsubscribeMaintenance) {
                unsubscribeMaintenance();
                unsubscribeMaintenance = null;
            }

            if (user) {
                // 1. Setup Global Maintenance & Force Refresh Listener (Only if logged in)
                try {
                    const maintenanceRef = doc(db, "settings", "maintenance");
                    unsubscribeMaintenance = onSnapshot(maintenanceRef, 
                        (snap) => {
                            if (snap.exists()) {
                                const data = snap.data();
                                const lastTrigger = localStorage.getItem('YELO_REFRESH_TRIGGER');
                                
                                if (data.refreshTrigger && data.refreshTrigger !== lastTrigger) {
                                    localStorage.setItem('YELO_REFRESH_TRIGGER', data.refreshTrigger);
                                    logger.warn("🚀 Force Refresh Triggered by Admin");
                                    
                                    localStorage.removeItem('YELO_USERS_CACHE');
                                    localStorage.removeItem('YELO_REGISTRATION_DATA');
                                    localStorage.removeItem('YELO_VEHICLE_EXPORT_DATA');
                                    
                                    window.location.reload(true);
                                }
                            }
                        },
                        (error) => {
                            // SILENT: Do nothing on error to avoid toast notifications
                            logger.debug("Maintenance listener status:", error.code);
                        }
                    );
                } catch (e) {
                    logger.debug("Maintenance listener could not be started:", e.message);
                }

                // CSRF Protection: Validate session and token
                const storedToken = sessionUtils.getSessionToken();
                const storedUid = sessionUtils.getStoredUid();
                
                // If no session exists, create one (handles page refresh after login)
                if (!storedToken || !storedUid) {
                    const newToken = sessionUtils.generateSessionToken();
                    sessionUtils.setSecureSession(newToken, user.uid);
                    logger.info('Session recreated after page refresh');
                }
                // Critical: Verify user identity matches stored session
                else if (storedUid !== user.uid) {
                    logger.error('CSRF Attack Detected: Session UID mismatch', {
                        expected: storedUid,
                        received: user.uid
                    });
                    await logout();
                    showAlert({
                        title: 'Security Alert',
                        message: 'Session validation failed. Please login again.',
                        buttonLabel: 'OK',
                        isError: true
                    });
                    return;
                }

                const docRef = doc(db, "users", user.uid);

                unsubscribeSnapshot = onSnapshot(docRef, async (docSnap) => {
                    if (docSnap.exists()) {
                        const userData = docSnap.data();

                        // Enhanced security checks
                        const isLocked = userData.isLocked === true || userData.isLocked === 'true';
                        const isForceLogout = userData.forceLogout === true || userData.forceLogout === 'true';

                        // IMPORTANT: Firestore might return a cached version first.
                        // If it says "locked" but it's from cache, we wait for the server update
                        // to avoid kicking out a user who was recently unlocked.
                        if (isLocked && docSnap.metadata.fromCache) {
                            // Keep the app usable while waiting: a blank page here looked like a
                            // dead site on 13 Sep 2026 (the server answer never came for an offline tab).
                            logger.info("Account shows locked in cache, waiting for server sync...");
                            setLoading(false);
                            return;
                        }

                        if (isLocked) {
                            await logout();
                            setLoading(false); // Ensure app doesn't stay in loading state
                            showAlert({
                                title: 'System Maintenance',
                                message: 'System is currently not working. Please contact the administrator.',
                                buttonLabel: 'OK',
                                isError: true
                            });
                            return;
                        }

                        if (isForceLogout) {
                            await logout();
                            showAlert({
                                title: 'Session Terminated',
                                message: 'Your session has been terminated by an administrator.',
                                buttonLabel: 'OK',
                                isError: true
                            });
                            return;
                        }

                        const userWithPermissions = {
                            ...user,
                            ...userData,
                            isLocked: isLocked,
                            // no list = no permission (the rules see it the same way); the role's
                            // defaults were shown before and every page they opened was refused (ADM-12)
                            permissions: profilePermissions(userData)
                        };
                        
                        setCurrentUser(userWithPermissions);
                        setUserRole(userData.role || 'viewer');
                        startSessionTimer(userData.role || 'viewer');
                    } else {
                        // Auto-create user profile with enhanced security.
                        // A never-provisioned account (i.e. someone who signed up on their
                        // own) gets ZERO permissions (role 'pending') so it can read no
                        // operational data until an admin grants access. Admin-provisioned
                        // users always already have a Firestore doc (User Management writes
                        // it), so they never reach this branch and keep their permissions.
                        // The owner email is bootstrapped as admin so a lost admin profile
                        // can never lock everyone out (13 Sep 2026 incident).
                        logger.warn("User profile not found in Firestore. Auto-creating with no permissions (pending)...");
                        const OWNER_EMAILS = ['mo.alamir52@gmail.com'];
                        const isOwnerEmail = OWNER_EMAILS.includes((user.email || '').toLowerCase());
                        const newProfile = {
                            email: user.email,
                            role: isOwnerEmail ? 'admin' : 'pending',
                            permissions: isOwnerEmail ? (DEFAULT_ROLES['admin']?.permissions || []) : [],
                            createdAt: new Date().toISOString(),
                            // the rules refuse a profile without it (isValidData): without this field the
                            // owner's lost-admin bootstrap could never actually be written (found 26 Sep 2026)
                            last_updated: new Date().toISOString(),
                            security: {
                                forceChangePassword: !isOwnerEmail,
                                createdBySystem: true,
                                lastPasswordChange: null
                            }
                        };
                        
                        try {
                            // Create-only: if a profile appeared meanwhile (a manager profile that the
                            // snapshot missed for a moment, an admin restoring it), never replace it.
                            // On 13 Sep 2026 a manager account was silently rebuilt as a viewer this way.
                            const fresh = await getDoc(docRef);
                            if (fresh.exists()) {
                                logger.warn("Profile appeared before auto-create ran; keeping it.");
                            } else {
                                await setDoc(docRef, newProfile, { merge: true });
                            }
                        } catch (err) {
                            logger.error("Failed to auto-create profile:", err);
                        }
                    }
                    setLoading(false);
                }, (error) => {
                    logger.error("Error listening to user profile:", error);
                    setLoading(false);
                });

            } else {
                setCurrentUser(null);
                setUserRole(null);
                setLoading(false);
            }
        });

        return () => {
            if (unsubscribeMaintenance) unsubscribeMaintenance();
            unsubscribeAuth();
            if (unsubscribeSnapshot) unsubscribeSnapshot();
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // login changes with the failed-attempt count; the context hands out a stable wrapper so every consumer
    // re-renders only when the user or the role really changes
    const loginRef = useRef(login);
    loginRef.current = login;
    const stableLogin = useCallback((...args) => loginRef.current(...args), []);
    const value = useMemo(() => ({
        currentUser,
        user: currentUser,
        userRole,
        login: stableLogin,
        logout,
        resetPassword,
    }), [currentUser, userRole, stableLogin, logout]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}